/**
 * Operators Page
 *
 * Management interface for factory operators with filtering,
 * pagination, and quick access to face enrollment status.
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  Filter,
  ScanFace,
  Camera,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Eye,
  UserX,
  Building2,
  Clock,
  Briefcase,
  X,
} from 'lucide-react';
import { useOperators } from '../../hooks/useOperators';
import { useDepartmentLookup } from '../../hooks/useDepartments';
import { useShiftLookup } from '../../hooks/useShifts';
import { usePositionLookup } from '../../hooks/usePositions';
import { OperatorForm } from '../../components/operators/OperatorForm';
import type { OperatorListItem } from '@guild/shared';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useMemo(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function OperatorsPage() {
  useDocumentTitle('Operators');
  // Filter state
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [shiftId, setShiftId] = useState<string>('');
  const [positionId, setPositionId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [enrollmentFilter, setEnrollmentFilter] = useState<'all' | 'enrolled' | 'not-enrolled'>('all');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState<OperatorListItem | null>(null);

  // Debounce search
  const debouncedSearch = useDebounce(search, 300);

  // Build query options
  const queryOptions = useMemo(() => {
    const options: {
      search?: string;
      departmentId?: string;
      shiftId?: string;
      positionId?: string;
      isActive?: boolean;
      hasEnrolledFace?: boolean;
      page: number;
      limit: number;
    } = { page, limit };

    if (debouncedSearch) options.search = debouncedSearch;
    if (departmentId) options.departmentId = departmentId;
    if (shiftId) options.shiftId = shiftId;
    if (positionId) options.positionId = positionId;
    if (statusFilter === 'active') options.isActive = true;
    if (statusFilter === 'inactive') options.isActive = false;
    if (enrollmentFilter === 'enrolled') options.hasEnrolledFace = true;
    if (enrollmentFilter === 'not-enrolled') options.hasEnrolledFace = false;

    return options;
  }, [debouncedSearch, departmentId, shiftId, positionId, statusFilter, enrollmentFilter, page, limit]);

  // Fetch data
  const { data: operatorsData, isLoading, error } = useOperators(queryOptions);
  const { data: departments } = useDepartmentLookup();
  const { data: shifts } = useShiftLookup();
  const { data: positions } = usePositionLookup();

  const operators = operatorsData?.data ?? [];
  const pagination = operatorsData?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const total = pagination?.total ?? 0;

  // Calculate showing range
  const showingFrom = total === 0 ? 0 : (page - 1) * limit + 1;
  const showingTo = Math.min(page * limit, total);

  // Handlers
  const handleOpenCreate = useCallback(() => {
    setEditingOperator(null);
    setIsFormOpen(true);
  }, []);

  const handleOpenEdit = useCallback((operator: OperatorListItem) => {
    setEditingOperator(operator);
    setIsFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingOperator(null);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setDepartmentId('');
    setShiftId('');
    setPositionId('');
    setStatusFilter('all');
    setEnrollmentFilter('all');
    setPage(1);
  }, []);

  const hasActiveFilters = Boolean(
    search || departmentId || shiftId || positionId || statusFilter !== 'all' || enrollmentFilter !== 'all'
  );

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('ellipsis');
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-guild-green to-guild-green/80 shadow-lg shadow-guild-green/20">
              <Users size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Operators
            </h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Manage factory operators and their face enrollment for video analysis
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="
            inline-flex items-center gap-2 rounded-xl
            bg-guild-green px-5 py-2.5
            text-sm font-semibold text-white
            shadow-lg shadow-guild-green/25
            transition-all duration-200
            hover:bg-guild-green/90 hover:shadow-xl hover:shadow-guild-green/30
            active:scale-[0.98]
          "
        >
          <Plus size={18} strokeWidth={2.5} />
          Add Operator
        </button>
      </div>

      {/* Filter Bar */}
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          {/* Search */}
          <div className="relative flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search by name, employee ID, or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="
                w-full rounded-xl border border-gray-200 bg-gray-50/50
                py-2.5 pl-10 pr-4
                text-sm text-gray-900 placeholder-gray-400
                transition-all duration-200
                focus:border-guild-green focus:bg-white focus:outline-none focus:ring-2 focus:ring-guild-green/20
              "
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Filter size={16} />
              <span className="hidden sm:inline">Filters:</span>
            </div>

            {/* Department Filter */}
            <div className="relative">
              <Building2
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <select
                value={departmentId}
                onChange={(e) => {
                  setDepartmentId(e.target.value);
                  setPage(1);
                }}
                className="
                  appearance-none rounded-lg border border-gray-200 bg-white
                  py-2 pl-9 pr-8
                  text-sm text-gray-700
                  transition-colors duration-200
                  hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                "
              >
                <option value="">All Departments</option>
                {departments?.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Shift Filter */}
            <div className="relative">
              <Clock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
              />
              <select
                value={shiftId}
                onChange={(e) => {
                  setShiftId(e.target.value);
                  setPage(1);
                }}
                className="
                  appearance-none rounded-lg border border-gray-200 bg-white
                  py-2 pl-9 pr-8
                  text-sm text-gray-700
                  transition-colors duration-200
                  hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                "
              >
                <option value="">All Shifts</option>
                {shifts?.map((shift) => (
                  <option key={shift.id} value={shift.id}>
                    {shift.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Position Filter */}
            <div className="relative">
              <Briefcase
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 pointer-events-none"
              />
              <select
                value={positionId}
                onChange={(e) => {
                  setPositionId(e.target.value);
                  setPage(1);
                }}
                className="
                  appearance-none rounded-lg border border-gray-200 bg-white
                  py-2 pl-9 pr-8
                  text-sm text-gray-700
                  transition-colors duration-200
                  hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                "
              >
                <option value="">All Positions</option>
                {positions?.map((pos) => (
                  <option key={pos.id} value={pos.id}>
                    {pos.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                setPage(1);
              }}
              className="
                appearance-none rounded-lg border border-gray-200 bg-white
                py-2 px-3 pr-8
                text-sm text-gray-700
                transition-colors duration-200
                hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              "
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            {/* Face Enrollment Filter */}
            <select
              value={enrollmentFilter}
              onChange={(e) => {
                setEnrollmentFilter(e.target.value as 'all' | 'enrolled' | 'not-enrolled');
                setPage(1);
              }}
              className="
                appearance-none rounded-lg border border-gray-200 bg-white
                py-2 px-3 pr-8
                text-sm text-gray-700
                transition-colors duration-200
                hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              "
            >
              <option value="all">All Enrollment</option>
              <option value="enrolled">Face Enrolled</option>
              <option value="not-enrolled">Not Enrolled</option>
            </select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="
                  flex items-center gap-1.5 rounded-lg
                  px-3 py-2
                  text-sm font-medium text-gray-500
                  transition-colors duration-200
                  hover:bg-gray-100 hover:text-gray-700
                "
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message="Failed to load operators" />
        ) : operators.length === 0 ? (
          <EmptyState hasFilters={hasActiveFilters} onClearFilters={handleClearFilters} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Employee ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Email
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Department
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Face Enrolled
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Photos
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {operators.map((operator, index) => (
                    <tr
                      key={operator.id}
                      className="group transition-colors duration-150 hover:bg-guild-mint/5"
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm font-medium text-gray-900">
                          {operator.employeeId}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/operators/${operator.id}`}
                          className="
                            font-semibold text-gray-900
                            transition-colors duration-200
                            hover:text-guild-green
                          "
                        >
                          {operator.firstName} {operator.lastName}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {operator.email || '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {operator.departmentName || (
                            <span className="text-gray-400 italic">Unassigned</span>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge isActive={operator.isActive} />
                      </td>
                      <td className="px-6 py-4">
                        <EnrollmentBadge isEnrolled={operator.hasEnrolledFace} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Camera size={14} className="text-gray-400" />
                          {operator.photoCount}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenEdit(operator)}
                            className="
                              rounded-lg p-2
                              text-gray-400
                              transition-all duration-200
                              hover:bg-gray-100 hover:text-gray-600
                            "
                            title="Edit operator"
                          >
                            <Pencil size={16} />
                          </button>
                          <Link
                            to={`/operators/${operator.id}`}
                            className="
                              rounded-lg p-2
                              text-gray-400
                              transition-all duration-200
                              hover:bg-guild-mint/20 hover:text-guild-green
                            "
                            title="View details"
                          >
                            <Eye size={16} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col items-center justify-between gap-4 border-t border-gray-100 bg-gray-50/30 px-6 py-4 sm:flex-row">
              <p className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-700">{showingFrom}</span> to{' '}
                <span className="font-medium text-gray-700">{showingTo}</span> of{' '}
                <span className="font-medium text-gray-700">{total}</span> operators
              </p>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="
                    flex items-center gap-1 rounded-lg px-3 py-2
                    text-sm font-medium text-gray-600
                    transition-colors duration-200
                    hover:bg-white hover:text-gray-900
                    disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent
                  "
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>

                <div className="flex items-center gap-1 px-2">
                  {getPageNumbers().map((pageNum, idx) =>
                    pageNum === 'ellipsis' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`
                          min-w-[36px] rounded-lg px-3 py-2
                          text-sm font-medium
                          transition-all duration-200
                          ${
                            page === pageNum
                              ? 'bg-guild-green text-white shadow-sm'
                              : 'text-gray-600 hover:bg-white hover:text-gray-900'
                          }
                        `}
                      >
                        {pageNum}
                      </button>
                    )
                  )}
                </div>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="
                    flex items-center gap-1 rounded-lg px-3 py-2
                    text-sm font-medium text-gray-600
                    transition-colors duration-200
                    hover:bg-white hover:text-gray-900
                    disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent
                  "
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Operator Form Modal */}
      {isFormOpen && (
        <OperatorForm
          operator={editingOperator}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
}

// Status Badge Component
function StatusBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Inactive
    </span>
  );
}

// Face Enrollment Badge Component
function EnrollmentBadge({ isEnrolled }: { isEnrolled: boolean }) {
  return isEnrolled ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
      <ScanFace size={12} />
      Enrolled
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500">
      <ScanFace size={12} />
      Not enrolled
    </span>
  );
}

// Loading State
function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-sm text-gray-500">Loading operators...</p>
    </div>
  );
}

// Error State
function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
        <UserX size={24} className="text-red-500" />
      </div>
      <p className="mt-4 text-sm font-medium text-gray-900">Something went wrong</p>
      <p className="mt-1 text-sm text-gray-500">{message}</p>
    </div>
  );
}

// Empty State
function EmptyState({
  hasFilters,
  onClearFilters,
}: {
  hasFilters: boolean;
  onClearFilters: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50">
        <Users size={32} className="text-gray-400" />
      </div>
      <p className="mt-4 text-sm font-medium text-gray-900">
        {hasFilters ? 'No operators found' : 'No operators yet'}
      </p>
      <p className="mt-1 text-sm text-gray-500">
        {hasFilters
          ? 'Try adjusting your filters to find what you\'re looking for.'
          : 'Add your first operator to get started with performance tracking.'}
      </p>
      {hasFilters && (
        <button
          onClick={onClearFilters}
          className="
            mt-4 rounded-lg px-4 py-2
            text-sm font-medium text-guild-green
            transition-colors duration-200
            hover:bg-guild-mint/20
          "
        >
          Clear all filters
        </button>
      )}
    </div>
  );
}

export default OperatorsPage;
